/**
 * Interim front-side destination photo lookup (IATA → image URL).
 *
 * "Por hora": the flight bilhete and trip card resolve their header photo in the
 * browser from this static map instead of a backend-provided field. The URLs are
 * Unsplash hotlinks; airports of one metro share a photo (GRU/CGH → São Paulo).
 *
 * The IATA→city *name* authority lives in `@voll-intelligence/domain`
 * (`cityByAirport`) — this map is only IATA→photo, the presentation catalog. When a
 * new destination gets a photo, add it here AND to the domain airport-cities map.
 *
 * TODO (ownership): swap these Unsplash hotlinks for our own CDN assets keyed by
 * IATA. Using Unsplash via the API requires photographer attribution; owning the
 * images removes that obligation. See voll-specs destination-images notes.
 */

import { AIRPORT_CITIES } from '@voll-intelligence/domain';

// One photo per metro; multiple airports of a metro point at the same URL.
const SAO_PAULO =
  'https://images.unsplash.com/photo-1554168848-228452c09d60?q=80&w=1080&auto=format&fit=crop';
const CAMPINAS =
  'https://images.unsplash.com/photo-1669681463977-ba7117feafd8?q=80&w=1080&auto=format&fit=crop';
const RIO_DE_JANEIRO =
  'https://images.unsplash.com/photo-1700674843611-a54f29244a2b?q=80&w=1080&auto=format&fit=crop';
const BRASILIA =
  'https://images.unsplash.com/photo-1625426078245-6911839409dd?q=80&w=1080&auto=format&fit=crop';
const BELO_HORIZONTE =
  'https://images.unsplash.com/photo-1519566714072-b3fa4739a29e?q=80&w=1080&auto=format&fit=crop';
const CURITIBA =
  'https://images.unsplash.com/photo-1765850261697-50a10fab3ee6?q=80&w=1080&auto=format&fit=crop';
const PORTO_ALEGRE =
  'https://images.unsplash.com/photo-1770509677763-6017ec9c258b?q=80&w=1080&auto=format&fit=crop';
const FLORIANOPOLIS =
  'https://images.unsplash.com/photo-1611158241900-e0ca6a75d57e?q=80&w=1080&auto=format&fit=crop';
const SALVADOR =
  'https://images.unsplash.com/photo-1615989387035-f78b2fc7c46d?q=80&w=1080&auto=format&fit=crop';
const RECIFE =
  'https://images.unsplash.com/photo-1612383892465-153167840470?q=80&w=1080&auto=format&fit=crop';
const FORTALEZA =
  'https://images.unsplash.com/photo-1615332236395-8b9a9e026817?q=80&w=1080&auto=format&fit=crop';
const NATAL =
  'https://images.unsplash.com/photo-1583662721557-c778b1fd74fd?q=80&w=1080&auto=format&fit=crop';
const LONDRINA =
  'https://images.unsplash.com/photo-1595596825246-8197d0ed5a3f?q=80&w=1080&auto=format&fit=crop';
const RIBEIRAO_PRETO =
  'https://images.unsplash.com/photo-1700680153552-b05500e71fd1?q=80&w=1080&auto=format&fit=crop';
const NOVA_YORK =
  'https://images.unsplash.com/photo-1781545808073-9ec0a8f1215a?q=80&w=1080&auto=format&fit=crop';
const NEWARK =
  'https://images.unsplash.com/photo-1655781723092-55bba63bdc0b?q=80&w=1080&auto=format&fit=crop';
const MIAMI =
  'https://images.unsplash.com/photo-1589083130544-0d6a2926e519?q=80&w=1080&auto=format&fit=crop';
const PARIS =
  'https://images.unsplash.com/photo-1550340499-a6c60fc8287c?q=80&w=1080&auto=format&fit=crop';
const BUENOS_AIRES =
  'https://images.unsplash.com/photo-1597061447004-d78ef784415b?q=80&w=1080&auto=format&fit=crop';

// IATA → Unsplash hotlink (imgix-sized for the card header). Interim — see file header.
const AIRPORT_IMAGE: Record<string, string> = {
  GRU: SAO_PAULO,
  CGH: SAO_PAULO,
  VCP: CAMPINAS,
  SDU: RIO_DE_JANEIRO,
  GIG: RIO_DE_JANEIRO,
  BSB: BRASILIA,
  CNF: BELO_HORIZONTE,
  PLU: BELO_HORIZONTE,
  CWB: CURITIBA,
  POA: PORTO_ALEGRE,
  FLN: FLORIANOPOLIS,
  SSA: SALVADOR,
  REC: RECIFE,
  FOR: FORTALEZA,
  NAT: NATAL,
  LDB: LONDRINA,
  RAO: RIBEIRAO_PRETO,
  JFK: NOVA_YORK,
  EWR: NEWARK,
  MIA: MIAMI,
  CDG: PARIS,
  EZE: BUENOS_AIRES,
  AEP: BUENOS_AIRES,
};

/**
 * Destination photo URL for an arrival IATA (the card's `flight.to`). Returns
 * `undefined` for unmapped airports — the card then falls back to its gradient.
 */
export function destinationImageByIata(iata?: string): string | undefined {
  if (!iata) return undefined;
  return AIRPORT_IMAGE[iata.trim().toUpperCase()];
}

/** Nome de cidade normalizado para o lookup reverso — acento e caixa fora. */
function citySlug(city: string): string {
  return city.normalize('NFD').replaceAll(/[̀-ͯ]/g, '').trim().toLowerCase();
}

// Cidade (nome canônico do domain) → foto, derivado do mapa por IATA acima.
const CITY_IMAGE: Record<string, string> = Object.fromEntries(
  Object.entries(AIRPORT_CITIES)
    .filter(([iata]) => iata in AIRPORT_IMAGE)
    .map(([iata, city]) => [citySlug(city), AIRPORT_IMAGE[iata] as string]),
);

/**
 * Destination photo by CITY NAME — a viagem só de hotel não tem IATA de
 * chegada, mas o hotel nomeia a cidade (a tela de aprovações resolve por aqui).
 * `undefined` para cidade fora do catálogo — o consumidor cai no fallback.
 */
export function destinationImageByCity(city?: string | null): string | undefined {
  if (!city) return undefined;
  return CITY_IMAGE[citySlug(city)];
}
